import { TableContainer, Table, TableBody, TableHead, TableRow, TableCell } from '@mui/material';
import { Deal, Product, ProductSubscription } from '../../shared/models';
import { CerebroLink, CerebroText } from '../../shared/components';
import { BooleanUtils, DateUtils, EnumUtils, FileUtils, NumberUtils } from '../../shared/utils';
import { SuccessFeeTierTableDisplay } from './SuccessFeeTierTableDisplay';
import { transformSubscriptionSuccessFeeTiers } from '../../shared/utils/html';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { SubscriptionActionButton } from './SubscriptionActionButton';

interface ProductSubscriptionDisplayProps {
	dealGuid: string | undefined;
	subscription: ProductSubscription;
	deal: Deal;
	product: Product | null;
	isClosedFunded: boolean;
	onEdit: (subscription: ProductSubscription) => void;
	onEditSuccessTiers: (subscription: ProductSubscription) => void;
	onEditPaymentTerms: (subscription: ProductSubscription) => void;
	onChangeProduct: (subscription: ProductSubscription) => void;
	onUpload: (subscription: ProductSubscription) => void;
	onDelete: (subscription: ProductSubscription) => void;
	onCreateInvoice: (subscription: ProductSubscription) => void;
}

export const ProductSubscriptionDisplay: React.FC<ProductSubscriptionDisplayProps> = ({
	dealGuid,
	subscription,
	deal,
	product,
	isClosedFunded,
	onEdit,
	onEditSuccessTiers,
	onEditPaymentTerms,
	onChangeProduct,
	onUpload,
	onDelete,
	onCreateInvoice,
}) => {
	const isDirectProduct = product && product.isDirectIntroProduct;
	const hasSuccessFee = subscription.successFeeType !== 'None';
	const hasContractAgreement = !isDirectProduct || hasSuccessFee;

	return (
		<TableContainer sx={{ marginBottom: 2 }}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Product</TableCell>
						<TableCell>Fees</TableCell>
						<TableCell>Next Billing Date</TableCell>
						<TableCell>Billing</TableCell>
						{!isClosedFunded && <TableCell></TableCell>}
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell>{product?.productName}</TableCell>
						<TableCell>
							{subscription.setupFee > 0 && (
								<CerebroText>
									<strong>Platform Access Fee: </strong>{' '}
									{NumberUtils.FormatCurrency(subscription.setupFee)}{' '}
									{subscription.subscriptionFrequency != 'OneTime'
										? EnumUtils.GetSubscriptionFrequencyDescription(
												subscription.subscriptionFrequency
										  )
										: ''}
								</CerebroText>
							)}

							{subscription.successFeeType == 'Tiered' &&
							subscription.successFeeTiers &&
							subscription.successFeeTiers.length > 0 ? (
								<SuccessFeeTierTableDisplay
									tiers={transformSubscriptionSuccessFeeTiers(subscription.successFeeTiers)}
								/>
							) : subscription.successFeeType == 'Flat' ? (
								<CerebroText>
									<strong>Success Fee: </strong> {subscription.basisPoints} bps
								</CerebroText>
							) : (
								<>--</>
							)}

							{subscription.cancellationFee && subscription.cancellationFee > 0 ? (
								<CerebroText>
									<strong>Cancellation Fee: </strong>{' '}
									{NumberUtils.FormatCurrency(subscription.cancellationFee)}{' '}
								</CerebroText>
							) : (
								<></>
							)}

							{subscription.minimumSuccessFee && subscription.minimumSuccessFee > 0 ? (
								<CerebroText>
									<strong>Minimum Success Fee: </strong>{' '}
									{NumberUtils.FormatCurrency(subscription.minimumSuccessFee)}{' '}
								</CerebroText>
							) : (
								<></>
							)}
						</TableCell>
						<TableCell>
							{subscription.nextBillingDate && subscription.product.subscriptionFrequency != 'OneTime'
								? DateUtils.DisplayDateOnly(subscription.nextBillingDate)
								: '--'}
						</TableCell>
						<TableCell>{BooleanUtils.YesNo(subscription.isBillingActive)}</TableCell>
						{!isClosedFunded && (
							<TableCell>
								<SubscriptionActionButton
									subscription={subscription}
									deal={deal}
									onEdit={onEdit}
									onEditSuccessTiers={onEditSuccessTiers}
									onEditPaymentTerms={onEditPaymentTerms}
									onChangeProduct={onChangeProduct}
									onUpload={onUpload}
									onDelete={onDelete}
									onCreateInvoice={onCreateInvoice}
								/>
							</TableCell>
						)}
					</TableRow>

					{subscription.agreementFileName && hasContractAgreement && (
						<TableRow>
							<TableCell colSpan={6}>
								<CerebroText>
									<CerebroLink
										variant="link"
										target="_blank"
										rel="noreferrer"
										href={CerebroApiEndpoints.Deals.SpecificSubscriptionDownload(
											dealGuid,
											subscription.guid
										)}
									>
										{FileUtils.getFileTypeIcon(subscription.agreementFileName)}{' '}
										{subscription.agreementFileName}
									</CerebroLink>{' '}
									was signed by {subscription.agreementSignedBy} on{' '}
									{DateUtils.DisplayDateOnly(subscription.agreementSignedDate?.toString())}
								</CerebroText>
							</TableCell>
						</TableRow>
					)}
					{!hasContractAgreement && (
						<Table>
							<TableCell colSpan={6}>
								<CerebroText>No contract is required for a direct lender intro.</CerebroText>
							</TableCell>
						</Table>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
